import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCheck,
  faCheckCircle,
  faExclamationCircle,
  faPencil,
  faSignature,
  faThermometerHalf,
  faThumbsUp,
  faTimesCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ActionButton, HeadingItem } from "components";
import { ReconcileReasonCard } from "components/item-verification";
import { TripContext } from "contexts";
import React, { FC, useContext } from "react";
import { useOrderItemService } from "services";
import { OrderType, ReconcileReasonType } from "services/enums";
import {
  CatchWeight,
  LineItem,
  Order,
  OrderItem,
  OrderItemChange,
} from "services/types";
import { IconOptions } from "types";
import "./item-card.scss";

type Props = {
  orderItemId: number;
  onDetailsClicked: (orderItem: OrderItem) => void;
  onVerifyClicked: (orderItem: OrderItem) => void;
  onApproveChangeClicked?: (orderItem: OrderItem) => void;
  showRightHandIcon?: boolean;
};

export const ItemCard: FC<Props> = ({
  orderItemId,
  onDetailsClicked,
  onVerifyClicked,
  onApproveChangeClicked = () => {},
  showRightHandIcon = true,
}) => {
  const { isOrderItemChangeComplete, isOrderItemComplete } =
    useOrderItemService();
  const tripContext = useContext(TripContext);

  const item: OrderItem =
    tripContext.orderItems.find((orderItem) => orderItem.id == orderItemId) ??
    ({} as OrderItem);
  const lineItem: LineItem =
    tripContext.lineItems.find((l) => l.id == item.lineItemId) ??
    ({} as LineItem);
  const order: Order =
    tripContext.orders.find((o) => o.id === item.orderId) ?? ({} as Order);
  const orderItemsChanges: OrderItemChange[] =
    tripContext.orderItemChanges.filter(
      (change) => change.orderItemId == item.id
    );
  const notApprovedOrderItemChanges: OrderItemChange[] =
    orderItemsChanges.filter((change) => change.isChangeApproved == false);
  const catchWeights: CatchWeight[] = tripContext.catchWeights.filter(
    (weight) => weight.orderItemId == item.id
  );
  const isSignedFor = order.signatureId != null;

  const renderRightHandIcon = (): IconOptions => {
    let icon: IconOptions = {} as IconOptions;
    let isNotComplete: OrderItemChange[] =
      (orderItemsChanges &&
        orderItemsChanges.filter((x) => !isOrderItemChangeComplete(x))) ??
      [];
    if (order.signatureId != null)
      icon = { icon: faSignature, size: "2x", color: "#EB5757" };
    else if (isOrderItemComplete(item)) {
      icon =
        isNotComplete.length > 0
          ? { icon: faTimesCircle, color: "#EB5757", size: "2x" }
          : { icon: faCheckCircle, color: "#76AB21", size: "2x" };
    } else if (isOrderItemComplete(item) && isNotComplete.length > 0) {
      icon = { icon: faTimesCircle, color: "#EB5757", size: "2x" };
    }
    return icon;
  };

  const renderSTC_or_DNS_Badge = (): React.ReactNode => {
    if (order.orderType === OrderType.SubjectToCount) {
      return <div className={"card-badge"}>STC</div>;
    } else {
      return (
        order.orderType === OrderType.dropAndStay && (
          <div className={"card-badge"}>DNS</div>
        )
      );
    }
  };

  const checkForCatchWeightReconcile: CatchWeight[] = catchWeights
    ? catchWeights.filter(
        (catchweight) =>
          catchweight.reconcileReasonId && catchweight.reconcileReasonId > 0
      )
    : [];

  const calculateDeliveredQuanitity = (item: OrderItem) => {
    let deliveredQuantity: number = 0;

    if (catchWeights.length > 0) {
      let numRefusedCatchWeightItems = checkForCatchWeightReconcile.filter(
        (cwItems) => [3, 4, 5, 6, 7, 9, 10].includes(cwItems.reconcileReasonId!)
      ).length; //this filters catch weight items that were refused
      deliveredQuantity = item.countedQuantity - numRefusedCatchWeightItems;
    } else {
      let orderItemChangeQuantity: number = 0;
      orderItemsChanges.forEach((change) => {
        if (change.reconcileReasonId != ReconcileReasonType.OverageKept)
          orderItemChangeQuantity += change.quantity;
      });
      let shippedQuantity: number =
        item.shippedQuantity > item.countedQuantity
          ? item.shippedQuantity
          : item.countedQuantity;
      deliveredQuantity = shippedQuantity - orderItemChangeQuantity;
    }
    return deliveredQuantity;
  };

  return (
    <div className={"search-result-card-material"}>
      <div
        onClick={() =>
          isSignedFor ? onDetailsClicked(item) : onVerifyClicked(item)
        }
      >
        <HeadingItem
          text={(
            lineItem.longDescription ??
            lineItem.description ??
            "Unknown Item"
          ).toLowerCase()}
          rightHandIcon={showRightHandIcon ? renderRightHandIcon() : undefined}
          leftHandRender={() => renderSTC_or_DNS_Badge()}
        />
        <div className={"item-card-content-container"}>
          <div className={"list-subheading"}>
            Ordered : {item.orderedQuantity}
          </div>
          <div className={"list-subheading"}>
            Shipped : {item.shippedQuantity}
          </div>
          {item.verifiedMethod && (
            <div className={"list-subheading"}>
              Delivered : {calculateDeliveredQuanitity(item)}
            </div>
          )}
          <div className={"item-card-content-temperature"}>
            <FontAwesomeIcon
              icon={faThermometerHalf as IconProp}
              color="Red"
              size="1x"
            />
            <div className={"list-subheading"} style={{ paddingLeft: "10px" }}>
              {lineItem?.temperature}
            </div>
          </div>
        </div>
        {((orderItemsChanges && orderItemsChanges.length > 0) ||
          (checkForCatchWeightReconcile &&
            checkForCatchWeightReconcile.length > 0)) && (
          <ReconcileReasonCard orderItemId={orderItemId} />
        )}
      </div>
      <div className={"action-row"}>
        <ActionButton
          icon={faExclamationCircle}
          buttonColor={"#3498DB"}
          buttonWidth={"100px"}
          onClick={() => onDetailsClicked(item)}
          buttonText={"Details"}
        />
        {!isSignedFor && (
          <ActionButton
            icon={item.verifiedMethod == null ? faCheck : faPencil}
            buttonColor={item.verifiedMethod == null ? "#EB5757" : "#F2994A"}
            buttonWidth={"100px"}
            onClick={() => onVerifyClicked(item)}
            buttonText={item.verifiedTime == null ? "Verify" : "Edit"}
          />
        )}
        {!isSignedFor && notApprovedOrderItemChanges.length > 0 && (
          <ActionButton
            icon={faThumbsUp}
            buttonColor={"#82BC24"}
            buttonWidth={"100px"}
            onClick={() => onApproveChangeClicked(item)}
            buttonText={"Approve"}
          />
        )}
      </div>
    </div>
  );
};
